import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import {
  Color,
  FieldSize,
  fill, Size,
  approvedUnitLabel,
  approvedUnitOf,
  approvedUnitsFor,
  defaultDecimals,
  dividerPlaces,
  interleaveDividers,
  discountColumnShown,
  formatNumeric,
  itemTableLayout,
  itemTableRegistry,
  lineAmountCents,
  lineQuantityCorrection,
  moveRow,
  parseDecimal,
  referenceTone,
  seededMinimum,
  t,
  unitOverrideOffered,
  type ApprovedUnit,
  type BomLineSource,
  type DiscountLevel,
  type DividerPlace,
  type DocumentType,
  type ItemTableColumn,
  type ItemTableRow,
  type LineDiscount,
  type LineQuantityCorrection,
  type SaleQuantityRule,
  type SearchFn,
  type StringKey,
  type TaxPreviewAnswer,
} from '@bms2/shared';
import { Button, Checkbox, Dropdown, NumericInput, Select, TextInput, Textarea } from '../controls';
import { Icon } from '../Icon';
import { useApprovedUnits } from '../../shell/ApprovedUnits';
import styles from './ItemTable.module.css';
import { Money } from '../Money';
import { DocumentTotals } from './DocumentTotals';
import { useAddNew } from '../../pages/records/AddNewHost';
import { removeLabel } from '../removeLabel';

const COLUMN_CLASS: Record<string, string | undefined> = {
  item: styles.colItem,
  description: styles.colDescription,
  qty: styles.colQty,
  budget: styles.colBudget,
  rate: styles.colRate,
  amount: styles.colAmount,
  account: styles.colAccount,
  tax: styles.colTax,
  discount: styles.colDiscount,
};

/** 🔒 The token each column's width reads (decision 0651); the table's narrowest width is their sum over the columns it draws. */
const COLUMN_TOKEN: Readonly<Record<string, string>> = {
  handle: '--item-table-handle-width',
  item: '--item-table-item-floor',
  qty: '--item-table-qty-width',
  budget: '--item-table-other-width',
  rate: '--item-table-rate-column',
  amount: '--item-table-amount-width',
  account: '--item-table-account-width',
  tax: '--item-table-tax-width',
  discount: '--item-table-discount-width',
  remove: '--item-table-remove-width',
};

/**
 * The width the grid's box has, in rem, read by a ResizeObserver so the budget
 * folds as the pane narrows (decision 0651). 🔨 Null until measured and outside
 * a browser, which folds nothing.
 */
function useBoxRem(): [(element: HTMLDivElement | null) => void, number | null] {
  const [box, setBox] = useState<HTMLDivElement | null>(null);
  const [rem, setRem] = useState<number | null>(null);
  useEffect(() => {
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setRem(box.clientWidth > 0 ? box.clientWidth / root : null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [box]);
  return [setBox, rem];
}

/**
 * Item Table (piece 4, decision 0181): the shared line-item grid takes a
 * column set from the page that hosts it and has no sales-or-purchase mode.
 * The registry supplies the document type's default set with its six locked
 * columns; Amount is qty × rate, less the line discount when that column is
 * on, computed and never edited; the reference line reads grey on a sales
 * document and amber on a purchase document when the rate is under catalog;
 * the Discount column exists only while `documents.discountLevel` is Line.
 */
export const itemTableDocumentedStates = ['quote', 'purchaseOrder', 'discountLine', 'discountFooter', 'bom', 'bomDividers', 'units', 'readOnly'] as const;

/** A row is a line, or a named group divider the lines beneath it read as their group (decision 0333). */
export type ItemRowKind = 'line' | 'divider';

/** A line as the grid edits it: the hosting document maps it onto its own line record. */
export interface ItemLineDraft {
  id: string;
  /** A divider draws as one full-width cell holding its name in `itemLabel`; absent, the row is a line (decision 0333). */
  kind?: ItemRowKind;
  itemId: string | null;
  itemLabel: string;
  /** The catalog SKU of the chosen Item, shown under its name and never typed. */
  sku?: string | null;
  /** The line's own text under the name and SKU: the Item's description as it stood when picked, edited as notes (decision 0303). */
  description: string;
  qty: number | null;
  /** The unit as the cell reads it; it is chosen from the approved list and never typed (decision 0439). */
  unit: string;
  /** The approved `Unit` the cell is standing on, set whenever the person picks one; the host resolves the Item's own. */
  unitId?: string | null;
  /** Integer cents; the document seeds it per the registry row. */
  rateCents: number | null;
  accountId?: string | null;
  accountLabel?: string;
  taxCategoryId?: string | null;
  taxLabel?: string;
  discount?: LineDiscount | null;
  /** On a Quote's lines, the BOM rows the priced line rolls up (decision 0243); what makes the line's margin floor computable. */
  bomLineIds?: readonly string[] | null;
  /** A free-text row names no catalog Item. */
  freeText: boolean;
  /** 🔒 Whether the line's Item is a Service, which is what draws the Budget (hours) cell (decision 0860). */
  service?: boolean;
  /** 🔒 The hours a service line's sale budgets (decision 0860); null where nothing is typed. */
  budgetedHours?: number | null;
  /** 🔒 An allowance the customer authorizes, billed at actuals from the job (decision 2612). */
  allowance?: boolean;
  /** 🔒 A line the customer may take or leave; only the lines taken carry to the order (decision 2650). */
  optional?: boolean;
  /** 🔒 Where a bill of materials' goods line comes from, Buy or From Stock (decision 3320); no other host sets it. */
  source?: BomLineSource;
  /** 🔒 The site a From Stock line is reserved at (decision 3321); no other host sets it. */
  stockSiteId?: string | null;
}

export interface ItemTableProps {
  documentType: DocumentType;
  /** The hosting page's column set; the registry's default when omitted. */
  columns?: readonly ItemTableColumn[];
  lines: readonly ItemLineDraft[];
  onChange: (lines: ItemLineDraft[]) => void;
  /** The `documents.discountLevel` setting; Footer removes the column entirely. */
  discountLevel: DiscountLevel;
  itemSearch: SearchFn;
  accountSearch?: SearchFn;
  /** The catalog price the reference line compares against, resolved by the document per the registry's reference key. */
  referenceCentsOf?: (line: ItemLineDraft) => number | null;
  /** A fresh line's rate, seeded by the document per the registry's seed key. */
  seedRateCents?: (itemId: string) => number | null;
  /** The chosen Item's SKU, resolved by the document as the rate is. */
  skuOf?: (itemId: string) => string | null;
  /** The chosen Item's own unit of sale, which the line reads locked (decision 0439). */
  unitOf?: (itemId: string) => string | null;
  /** The approved units the unit cell offers; the frame's own list when omitted, and none outside the frame. */
  approvedUnits?: readonly ApprovedUnit[];
  /** The chosen Item's own description, the line's text until edited (decision 0303). */
  descriptionOf?: (itemId: string) => string | null;
  /** 🔒 Whether the chosen Item is a Service, which draws the Budget (hours) cell on its line (decision 0860). */
  serviceOf?: (itemId: string) => boolean;
  /**
   * 🔒 The chosen Item's sale guards, its minimum and its increment in its own
   * unit (decision 1526). A sales host passes it, and the grid seeds the
   * minimum on a pick and rounds a committed quantity up to the next step,
   * saying so under the quantity. 🔨 A purchase host and the bill of materials
   * pass none, because the guards are sale guards (piece 16).
   */
  quantityRuleOf?: (itemId: string) => SaleQuantityRule | null;
  /** The tax categories a line can be given; the column is a Dropdown over them, defaulted from the Item. */
  taxSearch?: SearchFn;
  /**
   * 🔨 The tax the calculation worked out for this line, in integer cents, drawn
   * under the category and never editable (decision 0435). A document the plan
   * computes no tax for — a Quote, a Purchase Order — supplies nothing here and
   * the cell is the category alone.
   */
  taxAmountCentsOf?: (line: ItemLineDraft) => number | null;
  /** A host's own label for a column: a picker-added column the registry does not name, or a locked one the host reads differently. */
  columnLabels?: Readonly<Record<string, string>>;
  currencyCode?: string;
  /**
   * 🔒 The grid draws its lines and nothing edits them (decision 0573): every
   * cell is its value as text, no row carries a remove and the footer offers no
   * Add. The subtotal stays, because a list drawn without its total is a list
   * somebody has to add up. It is what the Deal's Bill of materials tile draws
   * each list with, in the columns that list's own page draws.
   *
   * 🔨 `onChange` is never called in this mode, so a host that passes it a
   * no-op is not silently discarding a person's typing.
   */
  readOnly?: boolean;
  /**
   * 🔒 Each line carries the Allowance mark under its description (decision
   * 2612): a box while editing and a word once read. A Service deal's quote
   * passes it, and every other host draws no mark.
   */
  allowances?: boolean;
  /**
   * 🔒 Each line carries the Optional mark under its description (decision
   * 2650): a box while editing and a word once read. A Quote passes it, and
   * every other host draws no mark.
   */
  optionals?: boolean;
  /**
   * 🔒 A host's own mark under a line's description (decision 3320): the bill
   * of materials draws a goods line's Buy or From Stock and its shelf there.
   * It is handed the line, whether the grid reads rather than edits, and the
   * line's own patch; a host that passes nothing draws nothing.
   */
  lineMark?: (line: ItemLineDraft, read: boolean, patch: (change: Partial<ItemLineDraft>) => void) => ReactNode;
  /**
   * 🔒 The tax preview's answer, which grows the footer's subtotal into the
   * totals box: Sub Total, Discount, the Tax row named for the state and the
   * rate, and Total (decision 0644). A host that computes no tax passes
   * nothing, and the footer keeps its one subtotal.
   */
  totals?: { answer: TaxPreviewAnswer | null; failure: string | null };
}

export const isDivider = (line: ItemLineDraft): boolean => line.kind === 'divider';

const columnLabelKey: Readonly<Record<string, StringKey>> = {
  item: 'item.column.item',
  description: 'item.column.description',
  qty: 'item.column.qty',
  budget: 'item.column.budget',
  unit: 'item.column.unit',
  rate: 'item.column.rate',
  amount: 'item.column.amount',
  account: 'item.column.account',
  tax: 'item.column.tax',
  discount: 'item.column.discount',
};

/** The sign a percentage reads with; a symbol rather than a sentence, so it is not a catalogue entry. */
const PERCENT_SIGN = '%';

let nextLine = 0;

export function newLine(freeText = false): ItemLineDraft {
  nextLine += 1;
  return {
    id: `line-${nextLine}`,
    itemId: null,
    itemLabel: '',
    description: '',
    qty: 1,
    unit: '',
    rateCents: null,
    freeText,
  };
}

/**
 * 🔨 A line nobody has typed into — the empty row a fresh document opens on
 * (decision 0447). The seeded quantity of 1 is not a typed value, so it is not
 * read here.
 */
export function isUntouchedLine(line: ItemLineDraft): boolean {
  return line.kind !== 'divider' && !line.itemId && !line.itemLabel.trim() && !line.description.trim() && line.rateCents === null;
}

/**
 * 🔨 The id a save sends for an existing line, or null for one that was
 * generated or added in this session and has never been stored (T540; one
 * body shared by the four document forms that write it).
 */
export function storedId(draft: ItemLineDraft): string | null {
  return draft.id && !draft.id.startsWith('line-') && !draft.id.startsWith('added-') ? draft.id : null;
}

/**
 * 🔨 Lines arriving from elsewhere — a Deal's BOM generated onto a Quote, an
 * order's uninvoiced remainder added to an Invoice — replace the untouched
 * opening row rather than stacking under it, so a generated grid does not
 * carry a stray blank line above what was generated.
 */
export function withGeneratedLines(held: readonly ItemLineDraft[], incoming: readonly ItemLineDraft[]): ItemLineDraft[] {
  return [...held.filter((line) => !isUntouchedLine(line)), ...incoming];
}

/** A fresh, unnamed divider; the host refuses a save while its name is blank. */
export function newDivider(): ItemLineDraft {
  return { ...newLine(false), kind: 'divider', qty: null };
}

/** A stored divider as the grid draws it: its name in the label, keyed to its own row (decision 0650). */
export function dividerDraft(divider: { id?: string; name: string }): ItemLineDraft {
  return { ...newDivider(), ...(divider.id ? { id: divider.id } : {}), itemLabel: divider.name };
}

/** 🔒 A document's stored lines with its dividers standing among them, in the one reading order (decision 0650). */
export function withDividers(lines: readonly ItemLineDraft[], dividers: readonly DividerPlace[] | null | undefined): ItemLineDraft[] {
  return interleaveDividers(lines, dividers ?? []).map((row) => (row.kind === 'line' ? row.line : dividerDraft({ name: row.name })));
}

/** Whether a divider on the grid still has no name, which every save refuses (decisions 0333, 0650). */
export function hasUnnamedDivider(drafts: readonly ItemLineDraft[]): boolean {
  return drafts.some((draft) => isDivider(draft) && !draft.itemLabel.trim());
}

/**
 * 🔒 The grid as a document's save sends it (decision 0650): the lines the save
 * keeps, each mapped to the document's own line input, and every divider at its
 * place among those lines. A line the save filters out takes no place, so a
 * divider stands where the person left it among the lines that were kept.
 */
export function savedRows<T>(drafts: readonly ItemLineDraft[], keep: (draft: ItemLineDraft) => boolean, toLine: (draft: ItemLineDraft) => T): { lines: T[]; dividers: DividerPlace[] } {
  const rows = drafts.flatMap((draft): ItemTableRow<ItemLineDraft>[] => (isDivider(draft) ? [{ kind: 'divider', name: draft.itemLabel.trim() }] : keep(draft) ? [{ kind: 'line', line: draft }] : []));
  const placed = dividerPlaces(rows);
  return { lines: placed.lines.map(toLine), dividers: placed.dividers };
}


const lineRemoveLabel = (line: ItemLineDraft): string => removeLabel('item.remove', line.itemLabel || line.description || line.id, ': ');

/** The keyboard step's label names the row it moves, as the remove button's does. */
function moveLabel(key: 'item.moveUp' | 'item.moveDown', line: ItemLineDraft): string {
  return `${t(key)}: ${line.itemLabel || line.description || line.id}`;
}

export function ItemTable({
  documentType,
  columns,
  lines,
  onChange,
  discountLevel,
  itemSearch,
  accountSearch,
  referenceCentsOf,
  seedRateCents,
  skuOf,
  unitOf,
  approvedUnits,
  descriptionOf,
  serviceOf,
  quantityRuleOf,
  taxSearch,
  taxAmountCentsOf,
  columnLabels,
  currencyCode = 'USD',
  readOnly = false,
  allowances = false,
  optionals = false,
  lineMark,
  totals,
}: ItemTableProps) {
  const addNew = useAddNew();
  const registry = itemTableRegistry[documentType];
  const showDiscount = discountColumnShown(discountLevel);
  // The description draws inside the item cell beneath the name (decision 0294; the drawing's ITEM DETAILS), and the unit inside the quantity cell beneath the quantity (decision 0648), so neither is a column of its own.
  const base = (columns ?? registry.defaultColumns).filter((c) => c !== 'discount' && c !== 'description' && c !== 'unit');
  const [picking, setPicking] = useState<Record<string, boolean>>({});
  // The rows whose unit the person has explicitly opened for a change; a line naming an Item reads it locked until then (decision 0439).
  const [overriding, setOverriding] = useState<Record<string, boolean>>({});
  // The frame supplies the approved units; a grid rendered outside it gets none and draws every unit locked rather than offering a list nobody seeded.
  const fromFrame = useApprovedUnits();
  const units = approvedUnits ?? fromFrame;
  // The row being dragged and the row the pointer is over; the drop moves the first to the second's place (decision 0333).
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  // 🔨 The row the grip is held on: a row is draggable only while its grip is held, so dragging across a box's text selects the text rather than moving the row (decision 0649).
  const [armed, setArmed] = useState<string | null>(null);
  // 🔒 The quantities the grid moved, by line, each read as a caption under the quantity until that line's quantity, unit or Item changes again (decision 1526).
  const [corrections, setCorrections] = useState<Record<string, (LineQuantityCorrection & { serial: number }) | undefined>>({});
  // The lines whose quantity the person has committed here; a 1 on any other line is the opening seed, never a typed value (decision 0447).
  const [typedQty, setTypedQty] = useState<Record<string, boolean>>({});
  // 🔨 Each correction remounts the quantity box, so it redraws the corrected figure even where the line already held that number.
  const serial = useRef(0);
  const shown: ItemTableColumn[] = showDiscount ? [...base.slice(0, base.indexOf('amount')), 'discount', ...base.slice(base.indexOf('amount'))] : [...base];
  // 🔒 The width budget (decision 0651): the pickers fold into the item cell, account first, as the box narrows.
  const [measureBox, boxRem] = useBoxRem();
  const layout = itemTableLayout(shown, boxRem, { handle: !readOnly, remove: !readOnly });
  const drawn = layout.drawn as ItemTableColumn[];
  const folded = layout.folded as ItemTableColumn[];
  // A host's own label wins, so the BOM host can read the description column as the line's group (decision 0291).
  const labelOf = (column: string): string => columnLabels?.[column] ?? (columnLabelKey[column] ? t(columnLabelKey[column]!) : column);

  const patch = (id: string, change: Partial<ItemLineDraft>) => onChange(lines.map((l) => (l.id === id ? { ...l, ...change } : l)));
  /** The caption a line carries after a change: the correction that moved it, or none. */
  const noteCorrection = (id: string, moved: LineQuantityCorrection | null) => {
    serial.current += 1;
    const next = serial.current;
    setCorrections((held) => ({ ...held, [id]: moved ? { ...moved, serial: next } : undefined }));
  };
  const ruleOf = (itemId: string | null): SaleQuantityRule | null => (itemId ? quantityRuleOf?.(itemId) ?? null : null);
  /**
   * 🔒 Leaving the Qty box (decision 1526): the committed quantity through the
   * Item's sale guards, never a keystroke. A quantity that moved takes the
   * corrected figure and the caption; one that did not drops any caption.
   * 🔨 A box left as it was drawn commits nothing: the caption stands and an
   * untyped line stays untyped, so its pick still seeds the minimum.
   */
  const commitQty = (line: ItemLineDraft, qty: number | null) => {
    const drawn = line.qty === null ? null : parseDecimal(formatNumeric(line.qty, defaultDecimals('number')));
    if ((qty === line.qty || qty === drawn) && !lineQuantityCorrection(line.qty, line.unit, ruleOf(line.itemId), units)) return;
    setTypedQty((held) => ({ ...held, [line.id]: true }));
    const moved = lineQuantityCorrection(qty, line.unit, ruleOf(line.itemId), units);
    noteCorrection(line.id, moved);
    patch(line.id, { qty: moved ? moved.quantity : qty });
  };
  /**
   * A picked Item onto a line, from the search or from Add New: the Item's
   * name, SKU, unit, rate and description, and its minimum where the line's
   * quantity is still the untyped opening 1. 🔒 A quantity somebody typed is
   * corrected by the Item's guards instead, never overwritten (decision 1526).
   */
  const pickItem = (line: ItemLineDraft, row: { id: string; line1: string }) => {
    setPicking((held) => ({ ...held, [line.id]: false }));
    // A fresh Item brings its own unit, so an override taken against the Item before it drops away (decision 0439).
    setOverriding((held) => ({ ...held, [line.id]: false }));
    const unit = unitOf?.(row.id) ?? line.unit;
    const rule = ruleOf(row.id);
    const untyped = line.qty === null || (line.qty === 1 && !typedQty[line.id]);
    const seeded = untyped ? seededMinimum(unit, rule, units) : null;
    const moved = seeded === null ? lineQuantityCorrection(line.qty, unit, rule, units) : null;
    // 🔨 The seed is the table's own guess, so it moves without a caption; only a typed number reads as corrected.
    noteCorrection(line.id, untyped ? null : moved);
    patch(line.id, {
      itemId: row.id,
      itemLabel: row.line1,
      sku: skuOf?.(row.id) ?? null,
      unit,
      unitId: null,
      qty: seeded ?? moved?.quantity ?? line.qty,
      // Picking a part brings in its default price and its own description (product owner, 2026-09-11; decision 0303).
      rateCents: seedRateCents?.(row.id) ?? line.rateCents ?? null,
      description: line.description.trim() ? line.description : descriptionOf?.(row.id) ?? '',
      // 🔒 A Service draws the budget cell; any other Item drops a budget the line held (decision 0860).
      service: serviceOf?.(row.id) ?? false,
      ...(serviceOf?.(row.id) ? {} : { budgetedHours: null }),
    });
  };
  const remove = (id: string) => onChange(lines.filter((l) => l.id !== id));
  const add = (freeText: boolean) => onChange([...lines, newLine(freeText)]);
  const addDivider = () => onChange([...lines, newDivider()]);
  const indexOf = (id: string) => lines.findIndex((l) => l.id === id);
  const move = (id: string, to: number) => {
    const from = indexOf(id);
    if (from < 0 || to < 0 || to >= lines.length || from === to) return;
    onChange(moveRow(lines, from, to));
  };
  const drop = (targetId: string) => (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    // jsdom and some browsers hand a drop no dataTransfer; the row dragged is remembered either way (as the board does, decision 0206).
    const id = event.dataTransfer?.getData('text/item-line-id') || dragging;
    setOver(null);
    setDragging(null);
    setArmed(null);
    if (id && id !== targetId) move(id, indexOf(targetId));
  };

  /**
   * What the rate reads beneath it: per the line's unit, by its abbreviation where
   * the unit is an approved one (decision 0648). A line with no unit reads nothing.
   */
  const perUnit = (line: ItemLineDraft): string | null => {
    const unit = line.unit.trim();
    if (!unit) return null;
    return `${t('item.perUnit')} ${approvedUnitOf(units, unit)?.abbreviation ?? unit}`;
  };

  /**
   * 🔒 The caption under a quantity the grid moved (decision 1526): what was
   * typed, or the minimum it was raised to, muted, with the step or the
   * minimum named in its title.
   */
  const renderCorrection = (line: ItemLineDraft) => {
    const moved = corrections[line.id];
    if (!moved) return null;
    const rule = ruleOf(line.itemId);
    const ruleUnit = rule ? approvedUnitOf(units, rule.unit)?.abbreviation ?? rule.unit : '';
    const inItemUnit = (n: number | null) => [String(n ?? ''), ruleUnit].filter(Boolean).join(' ');
    const text = moved.reason === 'minimum' ? fill(t('item.qtyRaised'), { minimum: inItemUnit(rule?.minimum ?? null) }) : fill(t('item.qtyRoundedUp'), { from: String(moved.from) });
    const why = moved.reason === 'minimum' ? fill(t('item.qtyRaisedWhy'), { minimum: inItemUnit(rule?.minimum ?? null) }) : fill(t('item.qtyRoundedUpWhy'), { step: inItemUnit(rule?.increment ?? null) });
    return (
      <span className={styles.corrected} data-line-corrected={moved.reason} title={why}>
        {text}
      </span>
    );
  };

  /**
   * The unit under the quantity (decisions 0439, 0648). 🔨 The unit is never
   * typed: a write-in line's is the approved list itself; a line naming an Item
   * reads that Item's own unit locked, and changes it only through the explicit
   * Change the unit action, which offers the approved units of that Item's
   * family alone.
   */
  const renderUnit = (line: ItemLineDraft) => {
    const offered = approvedUnitsFor(units, line);
    const standing = approvedUnitOf(units, line.unit);
    const options = offered.map((unit) => ({ value: unit.id, label: approvedUnitLabel(unit) }));
    const pick = (unitId: string) => {
      const chosen = units.find((unit) => unit.id === unitId) ?? null;
      const unit = chosen?.unitName ?? '';
      // 🔒 The minimum and the increment are in the Item's unit, so a new unit re-runs them on the standing quantity (decision 1526).
      const moved = lineQuantityCorrection(line.qty, unit, ruleOf(line.itemId), units);
      noteCorrection(line.id, moved);
      patch(line.id, { unitId: chosen?.id ?? null, unit, ...(moved ? { qty: moved.quantity } : {}) });
    };
    const list = (state: 'list' | 'override') => (
      <Select size={FieldSize.Column}
        label={t('item.column.unit')}
        labelHidden
        value={line.unitId ?? standing?.id ?? ''}
        options={options}
        placeholder={t('item.unitChoose')}
        onChange={pick}
        dataAttribute={{ 'data-line-unit': state }}
      />
    );
    if (line.freeText) return list('list');
    if (overriding[line.id] && options.length > 0) return list('override');
    return (
      <span className={styles.unitCell} data-line-unit="locked" data-unit={line.unit}>
        <span className={styles.unitName}>{standing?.unitName ?? line.unit}</span>
        {unitOverrideOffered(units, line) ? (
          <button type="button" className={styles.iconButton} aria-label={t('item.changeUnit')} onClick={() => setOverriding((held) => ({ ...held, [line.id]: true }))} data-line-change-unit="true">
            <Icon meaning="edit" size="inline" />
          </button>
        ) : null}
      </span>
    );
  };

  /**
   * 🔒 The pickers the budget folded, beneath the description in the item cell,
   * each under its own caption because no column header names it there
   * (decision 0651). The same control, or the same text, the column would hold.
   */
  const foldedFields = (line: ItemLineDraft, read: boolean) =>
    folded.length === 0 ? null : (
      <div className={styles.folded} data-line-folded={folded.join(' ')}>
        {/* 🔨 A folded budget is drawn on a service line alone, as its column's cell is (decision 0860). */}
        {folded.filter((column) => column !== 'budget' || line.service === true).map((column) => (
          <div key={column} className={styles.foldedField} data-folded-column={column}>
            <span className={styles.foldedLabel} aria-hidden="true">{labelOf(column)}</span>
            {read ? renderReadCell(column, line) : renderCell(column, line)}
          </div>
        ))}
      </div>
    );

  /** 🔒 The Allowance mark under the description, where the host offers it (decision 2612). */
  const allowanceMark = (line: ItemLineDraft, read: boolean) => {
    if (!allowances) return null;
    if (read) return line.allowance ? <span className={styles.allowance} data-line-allowance="true">{t('item.allowance')}</span> : null;
    return (
      <div className={styles.allowance} data-line-allowance={line.allowance ? 'true' : 'false'} title={t('item.allowanceHint')}>
        <Checkbox size={Size.Small} label={t('item.allowance')} checked={line.allowance === true} onChange={(checked) => patch(line.id, { allowance: checked })} name={`allowance-${line.id}`} />
      </div>
    );
  };

  /** 🔒 The Optional mark under the description, where the host offers it (decision 2650). */
  const optionalMark = (line: ItemLineDraft, read: boolean) => {
    if (!optionals) return null;
    if (read) return line.optional ? <span className={styles.optional} data-line-optional="true">{t('item.optional')}</span> : null;
    return (
      <div className={styles.optional} data-line-optional={line.optional ? 'true' : 'false'} title={t('item.optionalHint')}>
        <Checkbox size={Size.Small} label={t('item.optional')} checked={line.optional === true} onChange={(checked) => patch(line.id, { optional: checked })} name={`optional-${line.id}`} />
      </div>
    );
  };

  const renderCell = (column: ItemTableColumn, line: ItemLineDraft) => {
    switch (column) {
      case 'item': {
        // The item cell is the drawing's ITEM DETAILS (decision 0294): the
        // chosen Item's name, its SKU under it grey and never typed (Figure 17;
        // piece 4, found while building 2026-09-08), and the description box
        // beneath both. A chosen Item is changed through the pencil, which
        // brings the search back.
        // 🔨 The box is a free-text area, the Item's own description as it stood when picked and the line's notes after (decision 0303); never the group.
        // 🔒 It takes the Item column's whole width and grows with its text, one line when empty (T198; decision 0647): never a narrow box inside a wide cell.
        // 🔒 It draws on a light tint with the ghost text Description while empty, so it reads as a place to type, the same on every host (T201).
        const description = (
          <div className={styles.description} data-line-description="true">
            <Textarea size={FieldSize.Column} label={labelOf('description')} labelHidden value={line.description} onChange={(v) => patch(line.id, { description: v })} placeholder={t('item.column.description')} name={`description-${line.id}`} rows={1} />
          </div>
        );
        if (line.freeText) {
          return (
            <div className={styles.itemCell}>
              <TextInput size={FieldSize.Column} label={t('item.column.item')} labelHidden value={line.itemLabel} onChange={(v) => patch(line.id, { itemLabel: v })} placeholder={t('item.freeText')} />
              {description}
              {allowanceMark(line, false)}
              {optionalMark(line, false)}
              {foldedFields(line, false)}
            </div>
          );
        }
        const chosen = line.itemId !== null && !picking[line.id];
        return (
          <div className={styles.itemCell}>
            {chosen ? (
              <div className={styles.itemDetails}>
                <span className={styles.nameGroup}>
                  <span className={styles.itemName} data-line-item={line.itemId ?? ''}>{line.itemLabel}</span>
                  <button type="button" className={styles.iconButton} aria-label={t('item.changeItem')} onClick={() => setPicking((held) => ({ ...held, [line.id]: true }))} data-line-change-item="true">
                    <Icon meaning="edit" size="inline" />
                  </button>
                </span>
                {/* 🔒 The SKU reads on its own line under the name, grey and never typed, and the description box stands beneath both (decision 0640). */}
                {line.sku ? (
                  <span className={styles.sku} data-line-sku={line.sku}>
                    {t('item.sku')} {line.sku}
                  </span>
                ) : null}
              </div>
            ) : (
              <Dropdown size={FieldSize.Column}
                key={line.itemId ?? 'none'}
                label={t('item.column.item')}
                labelHidden
                entityLabel={t('item.entityItem')}
                search={itemSearch}
                value={line.itemLabel}
                // The search's pick and Add New's pick are one handler, so each seeds the same things (decision 1526).
                onSelect={(row) => pickItem(line, row)}
                onAddNew={() => addNew.open({ kind: 'record', recordType: 'Item' }, (row) => pickItem(line, row))}
              />
            )}
            {description}
            {allowanceMark(line, false)}
            {optionalMark(line, false)}
            {lineMark?.(line, false, (change) => patch(line.id, change))}
            {foldedFields(line, false)}
          </div>
        );
      }
      case 'description':
        return null;
      case 'qty':
        // 🔒 The quantity stacks over its unit, and the unit keeps its pencil (decision 0648; product owner, 2026-09-23: "place the Unit under Qty with the pencil and remove that column so we have more space").
        return (
          <div className={styles.qtyCell}>
            <NumericInput key={`qty-${corrections[line.id]?.serial ?? 0}`} size={FieldSize.Column} label={t('item.column.qty')} labelHidden kind="number" value={line.qty} onChange={(v) => commitQty(line, v)} />
            {renderUnit(line)}
            {renderCorrection(line)}
          </div>
        );
      case 'unit':
        return null;
      case 'budget':
        // 🔒 Budget (hours) on a service line alone (decision 0860); every other line's cell stands empty.
        return line.service ? (
          <div className={styles.qtyCell} data-line-budget="service">
            <NumericInput size={FieldSize.Column} label={t('item.column.budget')} labelHidden kind="number" value={line.budgetedHours ?? null} onChange={(v) => patch(line.id, { budgetedHours: v })} />
          </div>
        ) : (
          <span data-line-budget="none" />
        );
      case 'rate': {
        const reference = referenceCentsOf?.(line) ?? null;
        const tone = line.rateCents === null ? null : referenceTone(registry, line.rateCents, reference);
        // 🔒 The reference figure is the Money component inside its sentence (decision 0717).
        const referenceNode = reference === null ? null : <>{t(registry.side === 'purchase' ? 'item.reference.purchase' : 'item.reference.list')} <Money cents={reference} currencyCode={currencyCode} /></>;
        return (
          <div className={styles.rateCell}>
            <NumericInput size={FieldSize.Column} label={t('item.column.rate')} labelHidden kind="currency" value={line.rateCents} onChange={(v) => patch(line.id, { rateCents: v })} />
            {perUnit(line) ? (
              <span className={styles.perUnit} data-line-per-unit={line.unit}>
                {perUnit(line)}
              </span>
            ) : null}
            {tone ? (
              <span className={styles.reference} data-reference={tone}>
                {referenceNode}
              </span>
            ) : null}
          </div>
        );
      }
      case 'amount': {
        const amount = lineAmountCents(line.qty ?? 0, line.rateCents ?? 0, showDiscount && line.discount ? line.discount : undefined, currencyCode);
        return (
          <span className={styles.amount} data-amount={amount}>
            <Money cents={amount} currencyCode={currencyCode} />
          </span>
        );
      }
      case 'account':
        return (
          <Dropdown size={FieldSize.Column}
            key={line.accountId ?? 'none'}
            label={t('item.column.account')}
            labelHidden
            entityLabel={t('item.entityAccount')}
            search={accountSearch ?? itemSearch}
            value={line.accountLabel ?? ''}
            onSelect={(row) => patch(line.id, { accountId: row.id, accountLabel: row.line1 })}
            onAddNew={() => addNew.open({ kind: 'account' }, (row) => patch(line.id, { accountId: row.id, accountLabel: row.line1 }))}
          />
        );
      case 'tax': {
        // 🔨 The category is the control; the amount beside it is the calculation's and is never typed (decision 0435).
        const taxCents = taxAmountCentsOf?.(line) ?? null;
        return (
          <span className={styles.taxCell} data-tax-category={line.taxCategoryId ?? ''}>
            <Dropdown size={FieldSize.Column}
              key={line.taxCategoryId ?? 'none'}
              label={t('item.column.tax')}
              labelHidden
              entityLabel={t('item.entityTax')}
              search={taxSearch ?? (() => Promise.resolve([]))}
              minChars={0}
              value={line.taxLabel ?? ''}
              onSelect={(row) => patch(line.id, { taxCategoryId: row.id, taxLabel: row.line1 })}
              onAddNew={() => addNew.open({ kind: 'unbuilt', what: 'TaxCategory' }, (row) => patch(line.id, { taxCategoryId: row.id, taxLabel: row.line1 }))}
            />
            {taxCents === null ? null : (
              <span className={styles.reference} data-line-tax={taxCents}>
                <Money cents={taxCents} currencyCode={currencyCode} />
              </span>
            )}
          </span>
        );
      }
      case 'discount': {
        const kind = line.discount?.kind ?? 'Percentage';
        const value = line.discount ? (line.discount.kind === 'Percentage' ? line.discount.percent : line.discount.amount.amount) : null;
        return (
          <div className={styles.discountCell}>
            <select
              className={styles.discountKind}
              aria-label={t('item.discountKind')}
              value={kind}
              onChange={(e) => {
                const next = e.target.value as LineDiscount['kind'];
                patch(line.id, {
                  discount: next === 'Percentage' ? { kind: 'Percentage', percent: 0 } : { kind: 'Flat', amount: { amount: 0, currencyCode } },
                });
              }}
            >
              <option value="Percentage">{t('item.discount.percentage')}</option>
              <option value="Flat">{t('item.discount.flat')}</option>
            </select>
            <NumericInput size={FieldSize.Column}
              label={t('item.column.discount')}
              labelHidden
              kind={kind === 'Percentage' ? 'percent' : 'currency'}
              value={value}
              onChange={(v) =>
                patch(line.id, {
                  discount: v === null ? null : kind === 'Percentage' ? { kind: 'Percentage', percent: v } : { kind: 'Flat', amount: { amount: v, currencyCode } },
                })
              }
            />
          </div>
        );
      }
      default: {
        const text = String((line as unknown as Record<string, unknown>)[column] ?? '');
        return <span>{text}</span>;
      }
    }
  };

  /**
   * 🔒 One cell as text, for the read-only grid (decision 0573). Every column
   * the editing grid draws a control for reads here as the value that control
   * held: the Item's name with its SKU and its description beneath, the
   * quantity, the unit, and the money through the one shared render path.
   */
  const renderReadCell = (column: ItemTableColumn, line: ItemLineDraft) => {
    switch (column) {
      case 'item':
        return (
          <div className={styles.itemCell}>
            <span className={styles.itemDetails}>
              <span className={styles.nameGroup}>
                <span className={styles.itemName} data-line-item={line.itemId ?? ''}>{line.itemLabel}</span>
              </span>
              {line.sku ? (
                <span className={styles.sku} data-line-sku={line.sku}>
                  {t('item.sku')} {line.sku}
                </span>
              ) : null}
            </span>
            {line.description ? <span className={styles.readDescription}>{line.description}</span> : null}
            {allowanceMark(line, true)}
            {optionalMark(line, true)}
            {lineMark?.(line, true, () => undefined)}
            {foldedFields(line, true)}
          </div>
        );
      case 'description':
        return null;
      case 'qty':
        // The quantity over its unit, as the editing grid draws it (decision 0648).
        return (
          <div className={styles.qtyCell}>
            <span className={styles.readNumber}>{line.qty === null ? '' : String(line.qty)}</span>
            <span className={styles.unitCell} data-line-unit="read" data-unit={line.unit}>
              <span className={styles.unitName}>{approvedUnitOf(units, line.unit)?.unitName ?? line.unit}</span>
            </span>
          </div>
        );
      case 'unit':
        return null;
      case 'budget':
        // 🔒 The budget reads as its hours on a service line alone (decision 0860).
        return line.service && line.budgetedHours !== null && line.budgetedHours !== undefined ? (
          <span className={styles.readNumber} data-line-budget="service">{String(line.budgetedHours)}</span>
        ) : (
          <span data-line-budget="none" />
        );
      case 'rate':
        return line.rateCents === null ? null : (
          <div className={styles.rateCell}>
            <span className={styles.readNumber}><Money cents={line.rateCents} currencyCode={currencyCode} /></span>
            {perUnit(line) ? <span className={styles.perUnit} data-line-per-unit={line.unit}>{perUnit(line)}</span> : null}
          </div>
        );
      case 'amount': {
        const amount = lineAmountCents(line.qty ?? 0, line.rateCents ?? 0, showDiscount && line.discount ? line.discount : undefined, currencyCode);
        return (
          <span className={styles.amount} data-amount={amount}>
            <Money cents={amount} currencyCode={currencyCode} />
          </span>
        );
      }
      case 'account':
        return <span className={styles.readText}>{line.accountLabel ?? ''}</span>;
      case 'tax': {
        const taxCents = taxAmountCentsOf?.(line) ?? null;
        return (
          <span className={styles.taxCell} data-tax-category={line.taxCategoryId ?? ''}>
            <span className={styles.readText}>{line.taxLabel ?? ''}</span>
            {taxCents === null ? null : (
              <span className={styles.reference} data-line-tax={taxCents}>
                <Money cents={taxCents} currencyCode={currencyCode} />
              </span>
            )}
          </span>
        );
      }
      case 'discount': {
        if (!line.discount) return null;
        // 🔨 A percentage is not money and does not go through the money render path; a flat discount does (decision 0481).
        if (line.discount.kind === 'Percentage') {
          const percent = `${line.discount.percent}${PERCENT_SIGN}`;
          return <span className={styles.readNumber}>{percent}</span>;
        }
        return <span className={styles.readNumber}><Money cents={line.discount.amount.amount} currencyCode={currencyCode} /></span>;
      }
      default:
        return <span className={styles.readText}>{String((line as unknown as Record<string, unknown>)[column] ?? '')}</span>;
    }
  };

  // 🔒 Every editing grid carries the handle column, on every host (T195, T200; decision 0649).
  // 🔨 A read-only grid carries none: there is no order to change without a Save behind it.
  const handles = !readOnly;
  // 🔒 The narrowest the table draws: the drawn columns' budget and the Item column's floor, read from the tokens; below it the box scrolls (decision 0651).
  const minWidth = `calc(${[...(handles ? ['handle'] : []), ...drawn, ...(readOnly ? [] : ['remove'])].map((c) => `var(${COLUMN_TOKEN[c] ?? '--item-table-other-width'})`).join(' + ')})`;
  // A divider carries no money: the subtotal sums the lines alone.
  const subtotal = lines.filter((line) => !isDivider(line)).reduce((sum, line) => sum + lineAmountCents(line.qty ?? 0, line.rateCents ?? 0, showDiscount && line.discount ? line.discount : undefined, currencyCode), 0);

  /** The handle cell: the six-dot grip the pointer drags, and the two steps the keyboard takes (decision 0333). */
  const renderHandle = (line: ItemLineDraft, index: number) => (
    <td className={styles.handleCell} data-column="handle">
      <span className={styles.handle} aria-hidden="true" data-line-handle={line.id} onPointerDown={() => setArmed(line.id)} onPointerUp={() => setArmed(null)}>
        <Icon meaning="dragHandle" size="inline" />
      </span>
      <span className={styles.steps}>
        <button type="button" className={styles.iconButton} aria-label={moveLabel('item.moveUp', line)} onClick={() => move(line.id, index - 1)} disabled={index === 0} data-line-move="up">
          <Icon meaning="moveUp" size="inline" />
        </button>
        <button type="button" className={styles.iconButton} aria-label={moveLabel('item.moveDown', line)} onClick={() => move(line.id, index + 1)} disabled={index === lines.length - 1} data-line-move="down">
          <Icon meaning="moveDown" size="inline" />
        </button>
      </span>
    </td>
  );

  return (
    <div className={styles.wrap} data-item-table={documentType} data-item-table-read-only={readOnly ? 'true' : 'false'} data-discount-column={showDiscount ? 'true' : 'false'}>
      <div className={styles.scroll} ref={measureBox} data-item-table-folded={folded.join(' ')}>
        <table
          className={styles.table}
          style={{
            minWidth,
          }}
        >
          <colgroup>
            {handles ? <col className={styles.colHandle} /> : null}
            {drawn.map((c) => (
              <col key={c} className={COLUMN_CLASS[c] ?? styles.colOther} />
            ))}
            {readOnly ? null : <col className={styles.colRemove} />}
          </colgroup>
          <thead>
            <tr>
              {handles ? (
                <th scope="col" className={styles.handleCell} data-column="handle">
                  <span className={styles.hidden}>{t('item.dragHandle')}</span>
                </th>
              ) : null}
              {drawn.map((c) => (
                <th key={c} scope="col" className={styles[c] ?? ''} data-column={c} data-locked={registry.locked.includes(c as never) ? 'true' : 'false'}>
                  {labelOf(c)}
                </th>
              ))}
              {readOnly ? null : (
                <th scope="col" className={styles.removeCell}>
                  <span className={styles.hidden}>{t('item.remove')}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr
                key={line.id}
                className={[isDivider(line) ? styles.dividerRow : '', dragging === line.id ? styles.rowDragging : '', over === line.id && dragging !== line.id ? styles.rowOver : ''].filter(Boolean).join(' ')}
                data-line-id={line.id}
                data-line-kind={line.kind ?? 'line'}
                data-line-index={index}
                data-free-text={line.freeText ? 'true' : 'false'}
                {...(handles
                  ? {
                      draggable: armed === line.id,
                      onDragStart: (event: DragEvent<HTMLElement>) => {
                        event.dataTransfer?.setData('text/item-line-id', line.id);
                        setDragging(line.id);
                      },
                      onDragOver: (event: DragEvent<HTMLElement>) => {
                        event.preventDefault();
                        setOver(line.id);
                      },
                      onDragLeave: () => setOver((held) => (held === line.id ? null : held)),
                      onPointerUp: () => setArmed(null),
                      onDragEnd: () => {
                        setDragging(null);
                        setOver(null);
                        setArmed(null);
                      },
                      onDrop: drop(line.id),
                    }
                  : {})}
              >
                {handles ? renderHandle(line, index) : null}
                {isDivider(line) ? (
                  // A divider is one band across every column: its name, typed in place (decision 0333), and read as the band's label where nothing edits.
                  <td className={styles.dividerCell} colSpan={drawn.length} data-column="divider">
                    {readOnly ? <span className={styles.readDivider}>{line.itemLabel}</span> : <TextInput size={FieldSize.Column} label={t('item.dividerName')} labelHidden value={line.itemLabel} onChange={(v) => patch(line.id, { itemLabel: v })} placeholder={t('item.dividerName')} name={`divider-${line.id}`} />}
                  </td>
                ) : (
                  drawn.map((c) => (
                    <td key={c} className={styles[c] ?? ''} data-column={c}>
                      {readOnly ? renderReadCell(c, line) : renderCell(c, line)}
                    </td>
                  ))
                )}
                {readOnly ? null : (
                  <td className={styles.removeCell}>
                    <button type="button" className={styles.iconButton} aria-label={lineRemoveLabel(line)} onClick={() => remove(line.id)}>
                      <Icon meaning="remove" size="inline" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.footer}>
        {readOnly ? null : (
          <>
            <Button size={Size.Small} text={[t('item.addRow')]} actions={[() => add(false)]} />
            {registry.freeTextRows ? <Button size={Size.Small} color={Color.Inactive} text={[t('item.addFreeText')]} actions={[() => add(true)]} /> : null}
            {/* 🔒 Every editing grid offers a group divider, on every document as on the BOM (decisions 0333, 0650). */}
            <span data-item-table-action="addDivider">
              <Button size={Size.Small} color={Color.Inactive} text={[t('item.addDivider')]} actions={[addDivider]} />
            </span>
          </>
        )}
        {totals ? (
          <DocumentTotals answer={totals.answer} failure={totals.failure} fallbackSubtotalCents={subtotal} currencyCode={currencyCode} />
        ) : lines.length > 0 ? (
          <div className={styles.subtotal} data-item-table-subtotal={String(subtotal)}>
            <span className={styles.subtotalLabel}>{t('item.subtotal')}</span>
            <span className={styles.subtotalValue}><Money cents={subtotal} currencyCode={currencyCode} /></span>
          </div>
        ) : null}
      </div>
      {addNew.host}
    </div>
  );
}
